/**
 * Partners & Services Page
 * Insurance, finance providers, garages, marinas, commission rules
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import {
  ArrowLeft, Users, Plus, Edit, Trash2, Search,
  Building, DollarSign, Car, Shield, Wrench, Anchor,
  Globe, Phone, Mail, ExternalLink, CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PartnersServicesPage = () => {
  const [activeTab, setActiveTab] = useState('insurance');
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCountry, setFilterCountry] = useState('all');
  const [addDialog, setAddDialog] = useState({ open: false, type: null });
  const [newPartner, setNewPartner] = useState({
    name: '',
    type: '',
    countries: [],
    commission_rate: 0,
    contact_email: '',
    contact_phone: '',
    website: '',
    is_active: true
  });

  useEffect(() => {
    fetchPartners();
  }, [activeTab]);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/vehicles/partners`, {
        params: { partner_type: activeTab }
      });
      setPartners(response.data.partners || defaultPartners[activeTab] || []);
    } catch (error) {
      console.error('Failed to fetch partners:', error);
      setPartners(defaultPartners[activeTab] || []);
    } finally {
      setLoading(false);
    }
  };

  // Default partner data
  const defaultPartners = {
    insurance: [
      { id: '1', name: 'Sri Lanka Insurance Corporation', type: 'insurance', countries: ['SL'], commission_rate: 8, contact_email: 'partners@slic.lk', website: 'www.slic.lk', is_active: true, logo: '🏢', plans: ['Comprehensive', 'Third Party', 'Fire & Theft'] },
      { id: '2', name: 'Ceylinco Insurance', type: 'insurance', countries: ['SL'], commission_rate: 7.5, contact_email: 'business@ceylinco.lk', website: 'www.ceylincoinsurance.lk', is_active: true, logo: '🏦', plans: ['Full Cover', 'Basic', 'Commercial'] },
      { id: '3', name: 'AIA Insurance', type: 'insurance', countries: ['SL', 'UK'], commission_rate: 6, contact_email: 'partners@aia.com', website: 'www.aia.com', is_active: true, logo: '🌐', plans: ['Premium', 'Standard'] },
      { id: '4', name: 'Aviva', type: 'insurance', countries: ['UK', 'EU'], commission_rate: 5.5, contact_email: 'partners@aviva.co.uk', website: 'www.aviva.co.uk', is_active: true, logo: '🔵', plans: ['Comprehensive', 'Third Party'] }
    ],
    finance: [
      { id: '5', name: 'People\'s Leasing', type: 'finance', countries: ['SL'], commission_rate: 3, contact_email: 'leasing@peoplesleasing.lk', website: 'www.peoplesleasing.lk', is_active: true, logo: '💰', products: ['Auto Loans', 'Leasing'] },
      { id: '6', name: 'Commercial Credit', type: 'finance', countries: ['SL'], commission_rate: 2.5, contact_email: 'auto@commercialcredit.lk', website: 'www.commercialcredit.lk', is_active: true, logo: '💳', products: ['Vehicle Loans', 'Hire Purchase'] },
      { id: '7', name: 'HSBC Auto Finance', type: 'finance', countries: ['UK', 'EU'], commission_rate: 2, contact_email: 'autofinance@hsbc.co.uk', website: 'www.hsbc.co.uk', is_active: true, logo: '🏦', products: ['PCP', 'HP', 'Loans'] }
    ],
    garage: [
      { id: '8', name: 'Toyota Lanka Service', type: 'garage', countries: ['SL'], commission_rate: 5, contact_email: 'service@toyotalanka.lk', website: 'www.toyotalanka.lk', is_active: true, logo: '🔧', services: ['Full Service', 'Repairs', 'Parts'] },
      { id: '9', name: 'Stafford Motor Company', type: 'garage', countries: ['SL'], commission_rate: 4, contact_email: 'service@stafford.lk', website: 'www.stafford.lk', is_active: true, logo: '⚙️', services: ['Luxury Service', 'Bodywork'] },
      { id: '10', name: 'Halfords Autocentre', type: 'garage', countries: ['UK'], commission_rate: 4.5, contact_email: 'partners@halfords.co.uk', website: 'www.halfords.com', is_active: true, logo: '🛠️', services: ['MOT', 'Service', 'Tyres'] }
    ],
    marina: [
      { id: '11', name: 'Colombo Marina', type: 'marina', countries: ['SL'], commission_rate: 6, contact_email: 'info@colombomarina.lk', website: 'www.colombomarina.lk', is_active: true, logo: '⚓', services: ['Berthing', 'Maintenance', 'Storage'] },
      { id: '12', name: 'Brighton Marina', type: 'marina', countries: ['UK'], commission_rate: 5, contact_email: 'info@brightonmarina.co.uk', website: 'www.brightonmarina.co.uk', is_active: true, logo: '🚤', services: ['Moorings', 'Repairs', 'Chandlery'] }
    ]
  };

  const partnerTypes = [
    { slug: 'insurance', name: 'Insurance Providers', icon: Shield },
    { slug: 'finance', name: 'Finance Companies', icon: DollarSign },
    { slug: 'garage', name: 'Garages & Service', icon: Wrench },
    { slug: 'marina', name: 'Marinas', icon: Anchor }
  ];

  const handleAddPartner = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/vehicles/partners?admin_id=admin`, newPartner);
      fetchPartners();
      setAddDialog({ open: false, type: null });
      setNewPartner({
        name: '', type: '', countries: [], commission_rate: 0,
        contact_email: '', contact_phone: '', website: '', is_active: true
      });
    } catch (error) {
      console.error('Failed to add partner:', error);
      // Add locally for demo
      setPartners(prev => [...prev, { ...newPartner, id: Date.now().toString(), logo: '🏢' }]);
      setAddDialog({ open: false, type: null });
    }
  };

  const togglePartnerStatus = (partnerId) => {
    setPartners(prev => prev.map(p => 
      p.id === partnerId ? { ...p, is_active: !p.is_active } : p
    ));
  };

  const filteredPartners = partners.filter(p => {
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (filterCountry !== 'all' && !p.countries.includes(filterCountry)) return false;
    return true;
  });

  const CurrentIcon = partnerTypes.find(t => t.slug === activeTab)?.icon || Building;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/vehicles">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="h-6 w-6 text-blue-600" />
              Partners & Services
            </h1>
            <p className="text-gray-500">Insurance, finance, garages, and more</p>
          </div>
        </div>
        <Button onClick={() => setAddDialog({ open: true, type: activeTab })}>
          <Plus className="h-4 w-4 mr-2" />
          Add Partner
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {partnerTypes.map((type) => {
          const count = defaultPartners[type.slug]?.length || 0;
          const Icon = type.icon;
          return (
            <Card key={type.slug} className={activeTab === type.slug ? 'border-blue-500' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm text-gray-500">{type.name}</p>
                  </div>
                  <Icon className="h-8 w-8 text-gray-300" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search partners..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterCountry} onValueChange={setFilterCountry}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Countries</SelectItem>
            <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
            <SelectItem value="UK">🇬🇧 UK</SelectItem>
            <SelectItem value="EU">🇪🇺 EU</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {partnerTypes.map((type) => (
            <TabsTrigger key={type.slug} value={type.slug}>
              <type.icon className="h-4 w-4 mr-2" />
              {type.name}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredPartners.map((partner) => (
              <Card key={partner.id} className={!partner.is_active ? 'opacity-60' : ''}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{partner.logo}</span>
                      <div>
                        <h3 className="font-semibold text-lg">{partner.name}</h3>
                        <div className="flex gap-2 mt-1">
                          {partner.countries.map(c => (
                            <Badge key={c} variant="outline" className="text-xs">
                              {c === 'SL' ? '🇱🇰' : c === 'UK' ? '🇬🇧' : '🇪🇺'} {c}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={partner.is_active}
                      onCheckedChange={() => togglePartnerStatus(partner.id)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                    <div>
                      <p className="text-gray-500">Commission Rate</p>
                      <p className="font-semibold text-green-600">{partner.commission_rate}%</p>
                    </div>
                    <div>
                      <p className="text-gray-500">{partner.plans ? 'Plans' : partner.products ? 'Products' : 'Services'}</p>
                      <p className="font-medium">{(partner.plans || partner.products || partner.services)?.length || 0} available</p>
                    </div>
                  </div>

                  {(partner.plans || partner.products || partner.services) && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {(partner.plans || partner.products || partner.services).map((item) => (
                        <Badge key={item} variant="secondary" className="text-xs">{item}</Badge>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      {partner.contact_email && (
                        <a href={`mailto:${partner.contact_email}`} className="flex items-center gap-1 hover:text-blue-600">
                          <Mail className="h-3 w-3" />
                          Email
                        </a>
                      )}
                      {partner.website && (
                        <a href={`https://${partner.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-blue-600">
                          <ExternalLink className="h-3 w-3" />
                          Website
                        </a>
                      )}
                    </div>
                    <Button size="sm" variant="outline">
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Commission Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Commission Rules by Country
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                🇱🇰 Sri Lanka
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Insurance Lead</span>
                  <span className="font-medium">6-8%</span>
                </div>
                <div className="flex justify-between">
                  <span>Finance Lead</span>
                  <span className="font-medium">2-3%</span>
                </div>
                <div className="flex justify-between">
                  <span>Service Booking</span>
                  <span className="font-medium">4-5%</span>
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                🇬🇧 United Kingdom
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Insurance Lead</span>
                  <span className="font-medium">4-6%</span>
                </div>
                <div className="flex justify-between">
                  <span>Finance Lead</span>
                  <span className="font-medium">1.5-2%</span>
                </div>
                <div className="flex justify-between">
                  <span>Service Booking</span>
                  <span className="font-medium">3-4.5%</span>
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold flex items-center gap-2 mb-3">
                🇪🇺 European Union
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Insurance Lead</span>
                  <span className="font-medium">4-5%</span>
                </div>
                <div className="flex justify-between">
                  <span>Finance Lead</span>
                  <span className="font-medium">1.5-2%</span>
                </div>
                <div className="flex justify-between">
                  <span>Service Booking</span>
                  <span className="font-medium">3-4%</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Partner Dialog */}
      <Dialog open={addDialog.open} onOpenChange={(open) => setAddDialog({ open, type: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Partner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">Partner Name</Label>
              <Input
                value={newPartner.name}
                onChange={(e) => setNewPartner(p => ({ ...p, name: e.target.value }))}
                placeholder="Company name"
              />
            </div>
            <div>
              <Label className="mb-2 block">Type</Label>
              <Select
                value={newPartner.type || addDialog.type}
                onValueChange={(v) => setNewPartner(p => ({ ...p, type: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="insurance">Insurance Provider</SelectItem>
                  <SelectItem value="finance">Finance Company</SelectItem>
                  <SelectItem value="garage">Garage/Service</SelectItem>
                  <SelectItem value="marina">Marina</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Countries</Label>
              <div className="flex gap-2">
                {['SL', 'UK', 'EU'].map(c => (
                  <Button
                    key={c}
                    size="sm"
                    variant={newPartner.countries.includes(c) ? 'default' : 'outline'}
                    onClick={() => setNewPartner(p => ({
                      ...p,
                      countries: p.countries.includes(c)
                        ? p.countries.filter(x => x !== c)
                        : [...p.countries, c]
                    }))}
                  >
                    {c === 'SL' ? '🇱🇰' : c === 'UK' ? '🇬🇧' : '🇪🇺'} {c}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Commission Rate (%)</Label>
              <Input
                type="number"
                value={newPartner.commission_rate}
                onChange={(e) => setNewPartner(p => ({ ...p, commission_rate: parseFloat(e.target.value) }))}
                placeholder="5"
              />
            </div>
            <div>
              <Label className="mb-2 block">Contact Email</Label>
              <Input
                type="email"
                value={newPartner.contact_email}
                onChange={(e) => setNewPartner(p => ({ ...p, contact_email: e.target.value }))}
                placeholder="partners@company.com"
              />
            </div>
            <div>
              <Label className="mb-2 block">Website</Label>
              <Input
                value={newPartner.website}
                onChange={(e) => setNewPartner(p => ({ ...p, website: e.target.value }))}
                placeholder="www.company.com"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog({ open: false, type: null })}>
              Cancel
            </Button>
            <Button onClick={handleAddPartner} disabled={!newPartner.name}>
              <Plus className="h-4 w-4 mr-2" />
              Add Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnersServicesPage;
